import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box } from '@mui/material';
import KnowledgeGraphPanel from 'components/common/knowledge-graph/KnowledgeGraphPanel';

const MOCK_URL = '/dev-mock/knowledge-graph-data';

const MOCK_DATA = {
    nodes: [
        {
            llm_learning_node_id: 1,
            object_uid: 'uid-model-1',
            name: 'Marketing Analysis',
            description: 'Core marketing analysis model',
            node_type_name: 'Model',
            type: 'Model',
            node_color: '#CE1F6C',
            default_icon: null,
            enabled_in_concierge_ind: 'Y' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: null,
        },
        {
            llm_learning_node_id: 2,
            object_uid: 'uid-dim-1',
            name: 'Region',
            description: 'Geographic region dimension',
            node_type_name: 'Dimension',
            type: 'Dimension',
            node_color: '#0FB1E8',
            default_icon: null,
            enabled_in_concierge_ind: 'Y' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
        },
        {
            llm_learning_node_id: 3,
            object_uid: 'uid-dim-2',
            name: 'Product Category',
            description: null,
            node_type_name: 'Dimension',
            type: 'Dimension',
            node_color: '#0FB1E8',
            default_icon: null,
            enabled_in_concierge_ind: 'Y' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
        },
        {
            llm_learning_node_id: 4,
            object_uid: 'uid-dim-3',
            name: 'Channel',
            description: null,
            node_type_name: 'Dimension',
            type: 'Dimension',
            node_color: '#0FB1E8',
            default_icon: null,
            enabled_in_concierge_ind: 'N' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
        },
        {
            llm_learning_node_id: 5,
            object_uid: 'uid-meas-1',
            name: 'Revenue',
            description: 'Total revenue',
            node_type_name: 'Measure',
            type: 'Measure',
            node_color: '#075B7E',
            default_icon: null,
            enabled_in_concierge_ind: 'Y' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
        },
        {
            llm_learning_node_id: 6,
            object_uid: 'uid-meas-2',
            name: 'Units Sold',
            description: null,
            node_type_name: 'Measure',
            type: 'Measure',
            node_color: '#075B7E',
            default_icon: null,
            enabled_in_concierge_ind: 'Y' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
        },
        {
            llm_learning_node_id: 7,
            object_uid: 'uid-meas-3',
            name: 'Conversion Rate',
            description: null,
            node_type_name: 'Measure',
            type: 'Measure',
            node_color: '#075B7E',
            default_icon: null,
            enabled_in_concierge_ind: 'Y' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
        },
        {
            llm_learning_node_id: 8,
            object_uid: 'uid-meas-4',
            name: 'Customer Lifetime Value',
            description: null,
            node_type_name: 'Measure',
            type: 'Measure',
            node_color: '#075B7E',
            default_icon: null,
            enabled_in_concierge_ind: 'N' as const,
            source_type: 'manual',
            parent_llm_learning_node_id: 1,
            alert: {
                text: 'Definition conflicts with Customer Lifetime Value in the Sales model.',
                link: '/editor/element/8',
            },
        },
    ],
    edges: [
        // Model → satellites
        { llm_learning_edge_id: 1, source_node_uid: 'uid-model-1', target_node_uid: 'uid-dim-1' },
        { llm_learning_edge_id: 2, source_node_uid: 'uid-model-1', target_node_uid: 'uid-dim-2' },
        { llm_learning_edge_id: 3, source_node_uid: 'uid-model-1', target_node_uid: 'uid-dim-3' },
        { llm_learning_edge_id: 4, source_node_uid: 'uid-model-1', target_node_uid: 'uid-meas-1' },
        { llm_learning_edge_id: 5, source_node_uid: 'uid-model-1', target_node_uid: 'uid-meas-2' },
        { llm_learning_edge_id: 6, source_node_uid: 'uid-model-1', target_node_uid: 'uid-meas-3' },
        { llm_learning_edge_id: 7, source_node_uid: 'uid-model-1', target_node_uid: 'uid-meas-4' },
        // Region → Revenue, Units Sold, Conversion Rate
        { llm_learning_edge_id: 8, source_node_uid: 'uid-dim-1', target_node_uid: 'uid-meas-1' },
        { llm_learning_edge_id: 9, source_node_uid: 'uid-dim-1', target_node_uid: 'uid-meas-2' },
        { llm_learning_edge_id: 10, source_node_uid: 'uid-dim-1', target_node_uid: 'uid-meas-3' },
        // Product Category → Revenue, Units Sold, Customer Lifetime Value
        { llm_learning_edge_id: 11, source_node_uid: 'uid-dim-2', target_node_uid: 'uid-meas-1' },
        { llm_learning_edge_id: 12, source_node_uid: 'uid-dim-2', target_node_uid: 'uid-meas-2' },
        { llm_learning_edge_id: 13, source_node_uid: 'uid-dim-2', target_node_uid: 'uid-meas-4' },
        // Channel → Revenue, Conversion Rate
        { llm_learning_edge_id: 14, source_node_uid: 'uid-dim-3', target_node_uid: 'uid-meas-1' },
        { llm_learning_edge_id: 15, source_node_uid: 'uid-dim-3', target_node_uid: 'uid-meas-3' },
    ],
};

export default function DevKnowledgeGraph() {
    const queryClient = useQueryClient();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        queryClient.setQueryData(['knowledge-graph', 'graph-data', MOCK_URL], MOCK_DATA);
        queryClient.setQueryData(['knowledge-graph', 'panel-data', MOCK_URL], MOCK_DATA);
        setReady(true);
    }, [queryClient]);

    if (!ready) return null;

    return (
        <Box sx={{ maxWidth: '50%' }}>
            <KnowledgeGraphPanel dataUrl={MOCK_URL} />
        </Box>
    );
}
